import { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import { api } from '../api';

const Manage = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCar, setEditingCar] = useState(null);
  const [formData, setFormData] = useState(null);
  const [status, setStatus] = useState({ type: '', message: '' });

  useEffect(() => {
    fetchCars();
  }, []);

  const fetchCars = async () => {
    try {
      const response = await api('/api/cars');

      if (!response.ok) {
        throw new Error('Failed to fetch cars. Make sure the admin server is running (npm run server)');
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server error: Make sure the admin server is running (npm run server)');
      }

      const data = await response.json();
      setCars(data);
      setLoading(false);
    } catch (error) {
      setStatus({
        type: 'error',
        message: `Error loading cars: ${error.message}`
      });
      setLoading(false);
    }
  };

  const handleEdit = (car) => {
    setEditingCar(car.id);
    setFormData({
      make: car.make,
      model: car.model,
      year: car.year,
      rangeKm: car.rangeKm,
      batterySize: car.batterySize,
      batteryChemistry: car.batteryChemistry || '',
      batteryChemistryInfo: car.batteryChemistryInfo || '',
      maxChargingSpeed: car.maxChargingSpeed,
      maxChargingSpeedInfo: car.maxChargingSpeedInfo || '',
      drivetrain: car.drivetrain,
      wltpRange: car.wltpRange,
      power: car.power,
      weight: car.weight,
      weather: car.weather,
      season: car.season,
      temperature: car.temperature,
      tires: car.tires || '',
      testDate: car.testDate,
      youtubeUrl: car.youtubeUrl || ''
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = {
        ...formData,
        year: parseInt(formData.year),
        rangeKm: parseInt(formData.rangeKm),
        batterySize: parseFloat(formData.batterySize),
        maxChargingSpeed: parseInt(formData.maxChargingSpeed),
        wltpRange: parseInt(formData.wltpRange),
        power: parseInt(formData.power),
        weight: parseInt(formData.weight),
        temperature: parseInt(formData.temperature),
        // Only include info fields if they have content
        batteryChemistryInfo: formData.batteryChemistryInfo?.trim() || undefined,
        maxChargingSpeedInfo: formData.maxChargingSpeedInfo?.trim() || undefined
      };

      const response = await api(`/api/cars/${editingCar}`, {
        method: 'PUT',
        body: JSON.stringify(dataToSubmit)
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to update car');
        } else {
          throw new Error('Server error: Make sure the admin server is running (npm run server)');
        }
      }

      const result = await response.json();
      setStatus({
        type: 'success',
        message: 'Successfully updated car data!'
      });
      setEditingCar(null);
      setFormData(null);

      // Reload the cars list
      await fetchCars();
      window.scrollTo(0, 0);
    } catch (error) {
      setStatus({
        type: 'error',
        message: `Error: ${error.message}`
      });
      window.scrollTo(0, 0);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this car test result?')) {
      return;
    }

    try {
      const response = await api(`/api/cars/${id}`, { method: 'DELETE' });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const error = await response.json();
          throw new Error(error.error || 'Failed to delete car');
        } else {
          throw new Error('Server error: Make sure the admin server is running (npm run server)');
        }
      }

      const result = await response.json();
      setStatus({
        type: 'success',
        message: 'Successfully deleted car!'
      });

      // Reload the cars list
      await fetchCars();
      window.scrollTo(0, 0);
    } catch (error) {
      setStatus({
        type: 'error',
        message: `Error: ${error.message}`
      });
      window.scrollTo(0, 0);
    }
  };

  const handleCancel = () => {
    setEditingCar(null);
    setFormData(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4 flex items-center justify-center">
        <div className="text-teal-400 text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <SEO title="Administrer tester" path="/manage" noindex />
      <div className="max-w-6xl mx-auto">
        <div className="bg-slate-800 rounded-2xl shadow-2xl p-8 border border-teal-500 border-opacity-30">
          <div className="mb-8">
            <h1 className="text-4xl font-black text-teal-400 mb-2">Manage Test Results</h1>
            <p className="text-gray-300">Edit or delete existing car range test results</p>
          </div>

          <a
            href="/admin"
            className="inline-block mb-6 text-teal-400 hover:text-teal-300 font-semibold"
          >
            ← Back to Admin Panel
          </a>

          {status.message && (
            <div
              className={`mb-6 p-4 rounded-xl ${
                status.type === 'success'
                  ? 'bg-teal-500 bg-opacity-20 text-teal-300 border border-teal-500'
                  : 'bg-red-500 bg-opacity-20 text-red-300 border border-red-500'
              }`}
            >
              {status.message}
            </div>
          )}

          {editingCar && formData ? (
            <form onSubmit={handleUpdate} className="space-y-6 mb-8">
              <h2 className="text-2xl font-bold text-teal-400">Editing: {formData.make} {formData.model}</h2>

              {/* Vehicle Information */}
              <div className="bg-slate-700 bg-opacity-50 rounded-xl p-6 border border-teal-500 border-opacity-20">
                <h3 className="text-xl font-bold text-teal-400 mb-4">Vehicle Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Make *</label>
                    <input
                      type="text"
                      name="make"
                      value={formData.make}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Model *</label>
                    <input
                      type="text"
                      name="model"
                      value={formData.model}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Year *</label>
                    <input
                      type="number"
                      name="year"
                      value={formData.year}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Test Conditions */}
              <div className="bg-slate-700 bg-opacity-50 rounded-xl p-6 border border-teal-500 border-opacity-20">
                <h3 className="text-xl font-bold text-teal-400 mb-4">Test Conditions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Weather *</label>
                    <select
                      name="weather"
                      value={formData.weather}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                      <option value="sun">☀️ Sun</option>
                      <option value="rain">🌧️ Rain</option>
                      <option value="snow">❄️ Snow</option>
                      <option value="cloudy">☁️ Cloudy</option>
                      <option value="night">🌙 Night</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Season *</label>
                    <select
                      name="season"
                      value={formData.season}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                      <option value="summer">☀️ Summer</option>
                      <option value="winter">❄️ Winter</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Temperature (°C) *</label>
                    <input
                      type="number"
                      name="temperature"
                      value={formData.temperature}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Test Date *</label>
                    <input
                      type="date"
                      name="testDate"
                      value={formData.testDate}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Tires *</label>
                    <input
                      type="text"
                      name="tires"
                      value={formData.tires}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Test Results */}
              <div className="bg-slate-700 bg-opacity-50 rounded-xl p-6 border border-teal-500 border-opacity-20">
                <h3 className="text-xl font-bold text-teal-400 mb-4">Test Results</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Range (km) *</label>
                    <input
                      type="number"
                      name="rangeKm"
                      value={formData.rangeKm}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">YouTube Video URL</label>
                    <input
                      type="url"
                      name="youtubeUrl"
                      value={formData.youtubeUrl}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Vehicle Specs */}
              <div className="bg-slate-700 bg-opacity-50 rounded-xl p-6 border border-teal-500 border-opacity-20">
                <h3 className="text-xl font-bold text-teal-400 mb-4">Vehicle Specifications</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Battery Size (kWh) *</label>
                    <input
                      type="number"
                      name="batterySize"
                      value={formData.batterySize}
                      onChange={handleChange}
                      required
                      step="0.1"
                      className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Battery Chemistry *</label>
                    <select
                      name="batteryChemistry"
                      value={formData.batteryChemistry}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                      <option value="">Select chemistry...</option>
                      <option value="NMC">NMC (Nickel Manganese Cobalt)</option>
                      <option value="LFP">LFP (Lithium Iron Phosphate)</option>
                      <option value="NCA">NCA (Nickel Cobalt Aluminum)</option>
                      <option value="LTO">LTO (Lithium Titanate)</option>
                      <option value="Solid State">Solid State</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Battery Chemistry Info (Optional)</label>
                    <textarea
                      name="batteryChemistryInfo"
                      value={formData.batteryChemistryInfo}
                      onChange={handleChange}
                      rows="2"
                      className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Additional info about battery chemistry..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Max Charging Speed (kW) *</label>
                    <input
                      type="number"
                      name="maxChargingSpeed"
                      value={formData.maxChargingSpeed}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Max Charging Speed Info (Optional)</label>
                    <textarea
                      name="maxChargingSpeedInfo"
                      value={formData.maxChargingSpeedInfo}
                      onChange={handleChange}
                      rows="2"
                      className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Additional info about charging speed..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Drivetrain *</label>
                    <select
                      name="drivetrain"
                      value={formData.drivetrain}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                      <option value="AWD">AWD</option>
                      <option value="RWD">RWD</option>
                      <option value="FWD">FWD</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">WLTP Range (km) *</label>
                    <input
                      type="number"
                      name="wltpRange"
                      value={formData.wltpRange}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Power (hp) *</label>
                    <input
                      type="number"
                      name="power"
                      value={formData.power}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Weight (kg) *</label>
                    <input
                      type="number"
                      name="weight"
                      value={formData.weight}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-8 py-3 rounded-xl font-bold bg-slate-600 text-gray-300 hover:bg-slate-500 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-8 py-3 rounded-xl font-bold bg-teal-500 text-slate-900 hover:bg-teal-400 shadow-lg hover:shadow-teal-500/50 transition-all duration-200"
                >
                  Update Car
                </button>
              </div>
            </form>
          ) : null}

          {/* Cars List */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-teal-400 mb-4">All Test Results</h2>
            {cars.length === 0 ? (
              <p className="text-gray-400">No test results found.</p>
            ) : (
              cars.map((car) => (
                <div
                  key={car.id}
                  className="bg-slate-700 bg-opacity-50 rounded-xl p-6 border border-teal-500 border-opacity-20"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-xl font-bold text-gray-100">
                        {car.make} {car.model} ({car.year})
                      </h3>
                      <p className="text-gray-400 mt-2">
                        Range: {car.rangeKm} km • Weather: {car.weather} • Season: {car.season} • Temp: {car.temperature}°C
                      </p>
                      <p className="text-gray-400">
                        Test Date: {car.testDate}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(car)}
                        className="px-4 py-2 rounded-lg font-semibold bg-teal-500 text-slate-900 hover:bg-teal-400 transition-all duration-200"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(car.id)}
                        className="px-4 py-2 rounded-lg font-semibold bg-red-500 text-white hover:bg-red-600 transition-all duration-200"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Manage;
